import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

export interface LocaleInfo { code: string; name: string; nativeName: string; region?: string; review?: string; available?: boolean }
type Catalog = Record<string, string>;

export const interpolate = (text: string, vars?: Record<string, unknown>): string =>
  text.replace(/\{(\w+)\}/g, (m, k: string) => (vars?.[k] === undefined || vars[k] === null ? m : String(vars[k])));

export const humanize = (s: string): string => {
  const t = s.replace(/_(cents|bp|id|ids)$/, '').replace(/_/g, ' ').trim();
  return t.charAt(0).toUpperCase() + t.slice(1);
};

export interface I18n {
  locale: string;
  locales: LocaleInfo[];
  setLocale(locale: string): void;
  t(key: string, vars?: Record<string, unknown>): string;
  has(key: string): boolean;
  field(name: string): string;
  option(value: string): string;
  entity(name: string): string;
  tz: string;
  currency: string;
  setDefaults(d: { tz?: string; currency?: string }): void;
  money(cents: number | null | undefined, currency?: string): string;
  date(iso: string | null | undefined, opts?: Intl.DateTimeFormatOptions): string;
  time(iso: string | null | undefined): string;
  dateTime(iso: string | null | undefined): string;
  day(date: string, opts?: Intl.DateTimeFormatOptions): string;
  num(n: number): string;
}

const I18nContext = createContext<I18n | null>(null);

function pickInitial(): string {
  try {
    const saved = localStorage.getItem('gs_locale');
    if (saved) return saved;
  } catch { /* ignore */ }
  const q = new URLSearchParams(window.location.search).get('lang');
  return q || navigator.language || 'en';
}

export function I18nProvider({ children, fallback }: { children: ReactNode; fallback: ReactNode }) {
  const [locale, setLocaleState] = useState<string>(pickInitial);
  const [catalog, setCatalog] = useState<Catalog | null>(null);
  const [locales, setLocales] = useState<LocaleInfo[]>([]);
  const [defaults, setDefaultsState] = useState({ tz: Intl.DateTimeFormat().resolvedOptions().timeZone, currency: 'EUR' });

  useEffect(() => {
    fetch('/v1/public/locales').then((r) => r.json()).then((list: LocaleInfo[]) => {
      setLocales(list);
      const codes = list.filter((l) => l.available !== false).map((l) => l.code);
      if (!codes.includes(locale)) {
        const base = locale.split('-')[0];
        const match = codes.find((c) => c === base) ?? codes.find((c) => c.split('-')[0] === base);
        if (match && match !== locale) setLocaleState(match);
      }
    }).catch(() => undefined);
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetch(`/v1/i18n/${encodeURIComponent(locale)}`).then((r) => r.json()).then((c: Catalog) => { if (!cancelled) setCatalog(c); }).catch(() => setCatalog({}));
    document.documentElement.lang = locale;
    return () => { cancelled = true; };
  }, [locale]);

  const setLocale = useCallback((l: string) => {
    try { localStorage.setItem('gs_locale', l); } catch { /* ignore */ }
    setLocaleState(l);
  }, []);
  const setDefaults = useCallback((d: { tz?: string; currency?: string }) => {
    setDefaultsState((prev) => (d.tz === prev.tz && d.currency === prev.currency ? prev : { tz: d.tz ?? prev.tz, currency: d.currency ?? prev.currency }));
  }, []);

  const value = useMemo<I18n | null>(() => {
    if (!catalog) return null;
    const lookup = (key: string): string | undefined => catalog[`ui.${key}`] ?? catalog[key];
    // Plain "en" formats dates and times the European way (24-hour clock); en-US and other regional variants keep their own conventions.
    const fmtLocale = locale === 'en' ? 'en-GB' : locale;
    const fmt = (opts: Intl.DateTimeFormatOptions) => new Intl.DateTimeFormat(fmtLocale, { timeZone: defaults.tz, ...opts });
    return {
      locale, locales, setLocale, tz: defaults.tz, currency: defaults.currency, setDefaults,
      t: (key, vars) => { const v = lookup(key); return v === undefined ? humanize(key.split('.').pop() ?? key) : interpolate(v, vars); },
      has: (key) => lookup(key) !== undefined,
      field: (name) => lookup(`field.${name}`) ?? humanize(name),
      option: (v) => lookup(`option.${v}`) ?? humanize(v),
      entity: (name) => lookup(`entity.${name}`) ?? humanize(name),
      money: (cents, currency) => (cents === null || cents === undefined || Number.isNaN(Number(cents)) ? '—'
        : new Intl.NumberFormat(fmtLocale, { style: 'currency', currency: currency ?? defaults.currency }).format(Number(cents) / 100)),
      date: (iso, opts) => (iso ? fmt(opts ?? { dateStyle: 'medium' }).format(new Date(iso)) : '—'),
      time: (iso) => (iso ? fmt({ hour: '2-digit', minute: '2-digit' }).format(new Date(iso)) : '—'),
      dateTime: (iso) => (iso ? fmt({ dateStyle: 'medium', timeStyle: 'short' }).format(new Date(iso)) : '—'),
      day: (date, opts) => new Intl.DateTimeFormat(fmtLocale, { timeZone: 'UTC', ...(opts ?? { weekday: 'short', day: 'numeric', month: 'short' }) }).format(new Date(`${date}T12:00:00Z`)),
      num: (n) => new Intl.NumberFormat(fmtLocale).format(n),
    };
  }, [catalog, locale, locales, defaults, setLocale, setDefaults]);

  if (!value) return <>{fallback}</>;
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18n {
  const v = useContext(I18nContext);
  if (!v) throw new Error('I18nProvider missing');
  return v;
}
